import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDOPById, saveDOP, updateDOP, getDOPDropdowns, getBearerToken } from '../services/dopService';
import './Dashboard.css';

const DeliveryOfPossession = () => {
  const { dopId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!dopId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dropdowns, setDropdowns] = useState({
    properties: [],
    units: [],
    reservations: [],
    customers: [],
    dopTypes: [
      { value: 'Standard', label: 'Standard' },
      { value: 'MC', label: 'MC' },
      { value: 'ReadyForLongTerm', label: 'Ready For Long Term' },
    ],
    dopStatuses: [
      { value: 'Draft', label: 'Draft' },
      { value: 'Completed', label: 'Completed' },
    ],
  });

  const [formData, setFormData] = useState({
    resvNo: '',
    propertyNo: '',
    propertyName: '',
    unitNo: '',
    handOverDate: '',
    ewA_AccountNo: '',
    electricityMeterReading: '',
    waterMeterReading: '',
    btU_No: '',
    btU_Reading: '',
    carParkingNo: '',
    maintenanceFee: '',
    dopType: '',
    transactionDate: '',
    status: 'Draft',
    modifiedBy: '',
  });

  const [reservationDetails, setReservationDetails] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  /**
   * Fetch dropdowns on component mount
   */
  useEffect(() => {
    fetchDropdowns();
    if (isEditMode) {
      fetchDOPRecord();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dopId]);

  /**
   * Fetch DOP dropdowns
   */
  const fetchDropdowns = async () => {
    try {
      const token = getBearerToken();
      if (!token) {
        setError('Authorization token not found. Please log in.');
        return;
      }

      const response = await getDOPDropdowns(token);
      if (response && response.data) {
        setDropdowns((prev) => ({
          ...prev,
          properties: response.data.properties || [],
          units: response.data.units || [],
          reservations: response.data.reservations || [],
          customers: response.data.customers || [],
        }));
      }
    } catch (err) {
      console.error('Error fetching dropdowns:', err);
    }
  };

  /**
   * Fetch DOP record for editing
   */
  const fetchDOPRecord = async () => {
    setLoading(true);
    try {
      const token = getBearerToken();
      if (!token) {
        setError('Authorization token not found. Please log in.');
        return;
      }

      const response = await getDOPById(token, dopId);
      if (response && response.data) {
        const data = response.data;
        setFormData({
          resvNo: data.resvNo || '',
          propertyNo: data.propertyNo || '',
          propertyName: data.propertyName || '',
          unitNo: data.unitNo || '',
          handOverDate: data.handOverDate ? data.handOverDate.split('T')[0] : '',
          ewA_AccountNo: data.ewA_AccountNo || '',
          electricityMeterReading: data.electricityMeterReading || '',
          waterMeterReading: data.waterMeterReading || '',
          btU_No: data.btU_No || '',
          btU_Reading: data.btU_Reading || '',
          carParkingNo: data.carParkingNo || '',
          maintenanceFee: data.maintenanceFee ? data.maintenanceFee.split('T')[0] : '',
          dopType: data.exp1 || '',
          transactionDate: data.transactionDate ? data.transactionDate.split('T')[0] : '',
          status: data.status || 'Draft',
          modifiedBy: data.modifiedBy || '',
        });
        setReservationDetails(data);
      }
    } catch (err) {
      setError(`Failed to fetch DOP record: ${err.message}`);
      console.error('Error fetching DOP record:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const errors = {};

    if (!formData.dopType) {
      errors.dopType = 'DOP Type is required';
    }
    if (!formData.handOverDate) {
      errors.handOverDate = 'Hand Over Date is required';
    }
    if (!formData.carParkingNo) {
      errors.carParkingNo = 'Car Parking No is required';
    }
    if (!formData.status) {
      errors.status = 'DOP Status is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Auto-sync maintenance fee date with handover date if DOP type is Standard
    if (name === 'dopType' && value === 'Standard' && formData.handOverDate) {
      setFormData((prev) => ({
        ...prev,
        maintenanceFee: prev.handOverDate,
      }));
    }
    if (name === 'handOverDate' && formData.dopType === 'Standard') {
      setFormData((prev) => ({
        ...prev,
        maintenanceFee: value,
      }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix validation errors');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getBearerToken();
      if (!token) {
        setError('Authorization token not found. Please log in.');
        return;
      }

      const dopData = {
        ...formData,
        handOverDate: formData.handOverDate ? new Date(formData.handOverDate).toISOString() : '',
        maintenanceFee: formData.maintenanceFee ? new Date(formData.maintenanceFee).toISOString() : '',
        transactionDate: formData.transactionDate ? new Date(formData.transactionDate).toISOString() : '',
        exp1: formData.dopType,
      };

      if (isEditMode) {
        dopData.id = dopId;
        await updateDOP(token, dopData);
      } else {
        await saveDOP(token, dopData);
      }

      setSuccess(
        `DOP record ${isEditMode ? 'updated' : 'created'} successfully!`
      );

      // Navigate back to list after 2 seconds
      setTimeout(() => {
        navigate('/manage-dop');
      }, 2000);
    } catch (err) {
      setError(`Failed to save DOP record: ${err.message}`);
      console.error('Error saving DOP record:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle back button
   */
  const handleBack = () => {
    navigate('/manage-dop');
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <h1>
          {isEditMode ? 'Edit' : 'Add'} Delivery of Possession
          <small>{isEditMode ? 'Update DOP record' : 'Create new DOP record'}</small>
        </h1>
        <ol className="breadcrumb">
          <li><a href="#"><i className="fa fa-dashboard"></i>Home</a></li>
          <li><a href="#">Sales</a></li>
          <li className="active">Delivery of Possession</li>
        </ol>
      </section>

      <section className="content">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible">
            <button
              type="button"
              className="close"
              onClick={() => setError('')}
            >
              <span>&times;</span>
            </button>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="alert alert-success alert-dismissible">
            <button
              type="button"
              className="close"
              onClick={() => setSuccess('')}
            >
              <span>&times;</span>
            </button>
            <strong>Success!</strong> {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Search Criteria Section */}
          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">Search Criteria</h3>
            </div>
            <div className="box-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Property</label>
                    <select
                      className="form-control"
                      name="propertyNo"
                      value={formData.propertyNo}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Property</option>
                      {dropdowns.properties.map((prop) => (
                        <option key={prop.id} value={prop.id}>
                          {prop.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Unit No</label>
                    <select
                      className="form-control"
                      name="unitNo"
                      value={formData.unitNo}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Unit</option>
                      {dropdowns.units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Reservation No</label>
                    <select
                      className="form-control"
                      name="resvNo"
                      value={formData.resvNo}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Reservation</option>
                      {dropdowns.reservations.map((resv) => (
                        <option key={resv.id} value={resv.id}>
                          {resv.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Customer</label>
                    <select
                      className="form-control"
                      name="customer"
                      value=""
                      onChange={handleInputChange}
                    >
                      <option value="">Select Customer</option>
                      {dropdowns.customers.map((cust) => (
                        <option key={cust.id} value={cust.id}>
                          {cust.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation Details Section */}
          {reservationDetails && (
            <div className="box box-primary">
              <div className="box-header with-border">
                <h3 className="box-title">Reservation Details</h3>
              </div>
              <div className="box-body">
                <div className="row">
                  <div className="col-md-3">
                    <label><strong>Property:</strong></label>
                    <p>{reservationDetails.propertyName}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Unit No:</strong></label>
                    <p>{reservationDetails.unitNo}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Reservation No:</strong></label>
                    <p>{reservationDetails.resvNo}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Customer:</strong></label>
                    <p>Customer Details</p>
                  </div>
                </div>
                <div className="row" style={{ marginTop: '15px' }}>
                  <div className="col-md-3">
                    <label><strong>Status:</strong></label>
                    <p>{reservationDetails.status}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Amount Received:</strong></label>
                    <p>{reservationDetails.amountReceived}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Amount Received (%):</strong></label>
                    <p>{reservationDetails.percentReceived}</p>
                  </div>
                  <div className="col-md-3">
                    <label><strong>Customer Email:</strong></label>
                    <p>{reservationDetails.email}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Handover Details Section */}
          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">Handover Details</h3>
            </div>
            <div className="box-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>
                      DOP Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="dopType"
                      value={formData.dopType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select DOP Type</option>
                      {dropdowns.dopTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {validationErrors.dopType && (
                      <small className="form-text text-danger">
                        {validationErrors.dopType}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Transaction Date (dd/mm/yyyy)</label>
                    <input
                      type="date"
                      className="form-control"
                      name="transactionDate"
                      value={formData.transactionDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>
                      Hand Over Date <span className="text-danger">*</span> (dd/mm/yyyy)
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="handOverDate"
                      value={formData.handOverDate}
                      onChange={handleInputChange}
                      required
                    />
                    {validationErrors.handOverDate && (
                      <small className="form-text text-danger">
                        {validationErrors.handOverDate}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>EWA / Electricity Account No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="ewA_AccountNo"
                      value={formData.ewA_AccountNo}
                      onChange={handleInputChange}
                      maxLength="15"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Electricity Meter Reading</label>
                    <input
                      type="text"
                      className="form-control"
                      name="electricityMeterReading"
                      value={formData.electricityMeterReading}
                      onChange={handleInputChange}
                      maxLength="50"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Water Meter Reading</label>
                    <input
                      type="text"
                      className="form-control"
                      name="waterMeterReading"
                      value={formData.waterMeterReading}
                      onChange={handleInputChange}
                      maxLength="50"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>BTU Reading</label>
                    <input
                      type="text"
                      className="form-control"
                      name="btU_Reading"
                      value={formData.btU_Reading}
                      onChange={handleInputChange}
                      maxLength="15"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Car Parking Floor No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="btU_No"
                      value={formData.btU_No}
                      onChange={handleInputChange}
                      maxLength="15"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>
                      Car Parking No <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="carParkingNo"
                      value={formData.carParkingNo}
                      onChange={handleInputChange}
                      maxLength="50"
                      required
                    />
                    {validationErrors.carParkingNo && (
                      <small className="form-text text-danger">
                        {validationErrors.carParkingNo}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Maintenance Fee Period (dd/mm/yyyy)</label>
                    <input
                      type="date"
                      className="form-control"
                      name="maintenanceFee"
                      value={formData.maintenanceFee}
                      onChange={handleInputChange}
                      disabled={formData.dopType === 'Standard'}
                      style={{
                        backgroundColor:
                          formData.dopType === 'Standard' ? '#f8f9fa' : '',
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>
                      DOP Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      {dropdowns.dopStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    {validationErrors.status && (
                      <small className="form-text text-danger">
                        {validationErrors.status}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Buttons */}
            <div className="box-footer">
              <div className="row">
                <div className="col-md-12">
                  <button
                    type="submit"
                    className="btn btn-primary btn-space radius-5"
                    disabled={loading}
                  >
                    <i className="fa fa-save"></i>{' '}
                    {isEditMode ? 'Update DOP' : 'Create DOP'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-space radius-5"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    <i className="fa fa-arrow-left"></i> Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-default btn-space radius-5"
                    onClick={() => {
                      setFormData({
                        resvNo: '',
                        propertyNo: '',
                        propertyName: '',
                        unitNo: '',
                        handOverDate: '',
                        ewA_AccountNo: '',
                        electricityMeterReading: '',
                        waterMeterReading: '',
                        btU_No: '',
                        btU_Reading: '',
                        carParkingNo: '',
                        maintenanceFee: '',
                        dopType: '',
                        transactionDate: '',
                        status: 'Draft',
                        modifiedBy: '',
                      });
                      setValidationErrors({});
                    }}
                    disabled={loading}
                  >
                    <i className="fa fa-redo"></i> Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
};

export default DeliveryOfPossession;
